import React, {
  useRef, useEffect, useCallback, useState,
} from 'react';
import PropTypes from 'prop-types';

// A stable CSS family name for an overlay font file (the presigned URL changes
// each fetch, so key off the filename instead).
function familyFor(fileName) {
  if (!fileName) return null;
  return `overlayfont-${fileName.replace(/[^a-zA-Z0-9]/g, '_')}`;
}

// Preview-only token substitution. EC2 does the authoritative substitution at
// render time; here we just show the customer roughly what they'll get.
function resolveTemplate(template, firstName) {
  if (!template) return '';
  const name = firstName || '';
  return template
    .replace(/\{\{?\s*first_name_upper\s*\}?\}/g, name.toUpperCase())
    .replace(/\{\{?\s*first_name\s*\}?\}/g, name)
    .replace(/\{\{?\s*last_name\s*\}?\}/g, '')
    .replace(/\{\{?\s*grade\s*\}?\}/g, '')
    .replace(/\{\{?\s*grad_year\s*\}?\}/g, '')
    .trim();
}

const num = (v, d) => {
  const n = parseFloat(v);
  return Number.isNaN(n) ? d : n;
};

// Draws the student's real name into a foreground overlay's name zone, matching
// where/how EC2 will bake it: geometry is percent-of-canvas, and the font size
// auto-fits (binary search) inside the horizontal bounds — the same algorithm
// the EC2 renderer and the admin placement preview use. Renders nothing unless
// the overlay defines a name_template, so it is inert for plain overlays.
const NameOverlay = ({ foreground, firstName, fontFamily }) => {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  // The real overlay font, once loaded as a webfont; until then we draw with a
  // fallback so the name still shows (placement/size are already accurate).
  const [loadedFamily, setLoadedFamily] = useState(null);

  // Load the overlay's TrueType font (presigned URL from the API) as a webfont,
  // then redraw in the real typeface EC2 bakes. Best-effort: on any failure we
  // keep the fallback font. The shoobphoto bucket allows cross-origin GET.
  useEffect(() => {
    const url = foreground && foreground.nameFontUrl;
    const fileName = foreground && foreground.nameFont;
    const family = familyFor(fileName);
    if (!url || !family || typeof FontFace === 'undefined' || !document.fonts) {
      return undefined;
    }

    let cancelled = false;
    const face = new FontFace(family, `url(${url})`);
    face.load()
      .then((loaded) => {
        if (cancelled) return;
        document.fonts.add(loaded);
        setLoadedFamily(`'${family}'`);
      })
      .catch(() => { /* keep the fallback font */ });

    return () => { cancelled = true; };
  }, [foreground && foreground.nameFontUrl, foreground && foreground.nameFont]);

  const draw = useCallback(() => {
    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    if (!wrap || !canvas) return;

    const w = wrap.clientWidth;
    const h = wrap.clientHeight;
    if (!w || !h) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;

    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);

    const text = resolveTemplate(foreground.nameTemplate, firstName);
    if (!text) return;

    const color = foreground.nameColor || '#000000';
    const sizeMin = (num(foreground.nameSizeMin, 3) / 100) * h;
    const sizeMax = (num(foreground.nameSizeMax, 8) / 100) * h;
    const cx = (num(foreground.nameCenterX, 50) / 100) * w;
    const cy = (num(foreground.nameCenterY, 80) / 100) * h;
    const bl = (num(foreground.nameBoundLeft, 10) / 100) * w;
    const br = (num(foreground.nameBoundRight, 90) / 100) * w;
    const maxWidth = Math.max(1, br - bl);
    const family = loadedFamily || fontFamily || 'Arial, Helvetica, sans-serif';

    let lo = Math.max(1, Math.round(sizeMin));
    let hi = Math.max(1, Math.round(sizeMax));
    let size = lo;
    while (lo <= hi) {
      const mid = Math.floor((lo + hi) / 2);
      ctx.font = `${mid}px ${family}`;
      if (ctx.measureText(text).width <= maxWidth) {
        size = mid;
        lo = mid + 1;
      } else {
        hi = mid - 1;
      }
    }

    ctx.font = `${size}px ${family}`;
    ctx.fillStyle = color;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, cx, cy);
  }, [foreground, firstName, fontFamily, loadedFamily]);

  useEffect(() => {
    draw();
    const wrap = wrapRef.current;
    if (!wrap || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => draw());
    ro.observe(wrap);
    return () => ro.disconnect();
  }, [draw]);

  if (!foreground || !foreground.nameTemplate) return null;

  return (
    <div
      ref={wrapRef}
      style={{
        position: 'absolute', inset: 0, zIndex: 101, pointerEvents: 'none',
      }}
    >
      <canvas ref={canvasRef} />
    </div>
  );
};

NameOverlay.defaultProps = {
  firstName: '',
  fontFamily: null,
};

const numProp = PropTypes.oneOfType([PropTypes.number, PropTypes.string]);

NameOverlay.propTypes = {
  foreground: PropTypes.shape({
    nameTemplate: PropTypes.string,
    nameColor: PropTypes.string,
    nameFont: PropTypes.string,
    nameFontUrl: PropTypes.string,
    nameSizeMin: numProp,
    nameSizeMax: numProp,
    nameCenterX: numProp,
    nameCenterY: numProp,
    nameBoundLeft: numProp,
    nameBoundRight: numProp,
  }).isRequired,
  firstName: PropTypes.string,
  fontFamily: PropTypes.string,
};

export default NameOverlay;
